import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";

/**
 * Warns before unsaved edits are thrown away.
 *
 * Covers the three ways they can be lost:
 *  - navigating to another route (blocked via the data router's `useBlocker`)
 *  - closing/reloading the tab (native `beforeunload` prompt)
 *  - an in-page action that resets the form — wrap those with {@link confirm}
 *
 * The caller renders the dialog and calls {@link proceed} / {@link cancel}.
 */
export function useUnsavedChangesGuard(dirty: boolean) {
  // Pending in-page action, held until the user answers the dialog.
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );

  // Native prompt for tab close / reload, which no in-app dialog can intercept.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers need returnValue set to show their own prompt.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // A blocked navigation is released once the component stops being dirty (e.g.
  // after a successful save) rather than stranding the user on the page.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useEffect(() => {
    if (!dirty && blockerRef.current.state === "blocked") blockerRef.current.proceed();
  }, [dirty]);

  /**
   * Run `action`, or hold it behind the dialog when there are unsaved edits.
   * Use for in-page changes that would discard the form (e.g. re-scoping it).
   */
  const confirm = useCallback(
    (action: () => void) => {
      if (!dirty) {
        action();
        return;
      }
      // Stored in a thunk: a bare function would be treated as a state updater.
      setPendingAction(() => action);
    },
    [dirty],
  );

  const open = blocker.state === "blocked" || pendingAction !== null;

  /** Discard the changes and carry on with whatever was blocked. */
  const proceed = useCallback(() => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
      return;
    }
    if (blockerRef.current.state === "blocked") blockerRef.current.proceed();
  }, [pendingAction]);

  /** Dismiss the dialog and stay exactly where we are. */
  const cancel = useCallback(() => {
    setPendingAction(null);
    if (blockerRef.current.state === "blocked") blockerRef.current.reset();
  }, []);

  return { open, confirm, proceed, cancel };
}
